/**
 * Helpers for nested text overrides used by the admin CMS-lite.
 * Paths are dot-strings like "hero.title1" or "faq.items.0.q".
 * Empty strings are treated as "no override" (use the default).
 */

export const getPath = (obj, path) => {
  if (!obj) return undefined;
  return path.split(".").reduce((acc, key) => {
    if (acc === undefined || acc === null) return undefined;
    return acc[key];
  }, obj);
};

export const setPath = (obj, path, value) => {
  const keys = path.split(".");
  const next = Array.isArray(obj) ? [...obj] : { ...obj };
  let cursor = next;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    const child = cursor[k];
    // Re-clone shallowly along the path
    if (Array.isArray(child)) {
      cursor[k] = [...child];
    } else if (child && typeof child === "object") {
      cursor[k] = { ...child };
    } else {
      cursor[k] = /^\d+$/.test(keys[i + 1]) ? [] : {};
    }
    cursor = cursor[k];
  }
  cursor[keys[keys.length - 1]] = value;
  return next;
};

/** Recursively remove empty strings, empty arrays, and empty objects. */
export const pruneEmpty = (val) => {
  if (val === "" || val === null || val === undefined) return undefined;
  if (Array.isArray(val)) {
    const cleaned = val
      .map((v) => pruneEmpty(v))
      .filter((v) => v !== undefined);
    return cleaned.length ? cleaned : undefined;
  }
  if (typeof val === "object") {
    const out = {};
    for (const [k, v] of Object.entries(val)) {
      const cv = pruneEmpty(v);
      if (cv !== undefined) out[k] = cv;
    }
    return Object.keys(out).length ? out : undefined;
  }
  return val;
};

/**
 * Deep-merge `overrides` into `base`. Strings/numbers in overrides win.
 * Arrays in overrides REPLACE the whole array at that path? No — we merge
 * element by element when both are arrays of objects (typical for FAQ items).
 */
export const deepMergeOverrides = (base, overrides) => {
  if (overrides === undefined || overrides === null) return base;
  if (Array.isArray(base) && Array.isArray(overrides)) {
    return base.map((item, i) => deepMergeOverrides(item, overrides[i]));
  }
  if (
    base &&
    overrides &&
    typeof base === "object" &&
    typeof overrides === "object" &&
    !Array.isArray(base) &&
    !Array.isArray(overrides)
  ) {
    const out = { ...base };
    for (const k of Object.keys(overrides)) {
      out[k] = deepMergeOverrides(base?.[k], overrides[k]);
    }
    return out;
  }
  // Primitive override (string/number) — return override if defined
  return overrides;
};
